import type { Key } from "antd/es/table/interface";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { ProductProfileProductEntity } from "src/app/features/ProductProfile/types/subTypes/ProductProfileProductEntity";
import { ActionBar } from "../../../ActionBar/ActionBar";
import { SecondaryButton } from "../../../Buttons/components/ActionButton";
import type { ExtendedColumnsType } from "../../../Table";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import { displayEnumValue } from "../../../Table/components/PropertyTable/transformations/cellTransformations";
import { Checkbox } from "../Checkbox/Checkbox";

const getColumnsConfig = (
    t: Function,
    onToggle: (id: string | number, key: keyof ProductProfileProductEntity, value: boolean | string) => void,
    showForceValue: boolean,
    readOnly: boolean,
): ExtendedColumnsType<ProductProfileProductEntity>[] => {
    const columns: ExtendedColumnsType<ProductProfileProductEntity>[] = [
        {
            title: t("productList.id"),
            key: "productIdColumn",
            dataIndex: "productId",
        },
        {
            title: t("productList.grCode"),
            key: "grCodeColumn",
            dataIndex: "grCode",
        },
        {
            title: t("productList.name"),
            key: "nameColumn",
            dataIndex: "name",
        },
        {
            title: t("productList.shortName"),
            key: "shortNameColumn",
            dataIndex: "shortName",
        },
        {
            title: t("productList.type"),
            key: "typeColumn",
            dataIndex: "type",
            render: (value) => displayEnumValue(value, "product-type"),
        },
        {
            title: t("productList.itemType"),
            key: "itemTypeColumn",
            dataIndex: "itemType",
            render: (value) => displayEnumValue(value, "product-item-type"),
        },
        {
            title: t("productList.vat"),
            key: "vatCodeColumn",
            dataIndex: "vatCode",
        },
    ];

    columns.push(
        {
            title: t("productList.enabled"),
            key: "enabledColumn",
            dataIndex: "enabled",
            render: (_, record) => {
                const recordId = record.productId;
                return (
                    <Checkbox
                        name={`enabled-${recordId}`}
                        label=""
                        value={record.enabled ?? false}
                        readOnly={readOnly}
                        onChange={(checked) => onToggle(recordId!, "enabled", checked)}
                    />
                );
            },
        },
        {
            title: t("productList.mandat"),
            key: "mandatoryColumn",
            dataIndex: "mandatory",
            render: (_, record) => {
                const recordId = record.productId;
                return (
                    <Checkbox
                        name={`mandatory-${recordId}`}
                        label=""
                        value={record.mandatory ?? false}
                        readOnly={!record.enabled || readOnly}
                        onChange={(checked) => onToggle(recordId!, "mandatory", checked)}
                    />
                );
            },
        },
        {
            title: t("productList.currentValue"),
            key: "inheritanceTypeColumn",
            dataIndex: "inheritanceType",
            render: (value = "PROFILE") => {
                return t(`enum.inheritanceUnitType.${value.toLowerCase()}`);
            },
        },
    );

    if (showForceValue) {
        columns.push({
            title: t("productList.forceValue"),
            key: "forceValueColumn",
            dataIndex: "forceValue",
            render: (_, record) => {
                const recordId = record.productId;
                return (
                    <Checkbox
                        name={`force-${recordId}`}
                        label=""
                        value={record.forceValue ?? false}
                        readOnly={readOnly}
                        onChange={(checked) => onToggle(recordId!, "forceValue", checked)}
                    />
                );
            },
        });
    }

    return columns;
};

type OrgUnitsProductsProfileTableProps = {
    products?: ProductProfileProductEntity[];
    onProductsChange?: (products: ProductProfileProductEntity[]) => void;
    showForceValue?: boolean;
    readOnly?: boolean;
    onBulkProfileValue?: (productIds: number[]) => void;
    onBulkParentValue?: (productIds: number[]) => void;
    productProfileId?: number | null;
    childProductProfileId?: number | null;
};

export const OrgUnitsProductsProfileTable: FC<OrgUnitsProductsProfileTableProps> = ({
    products = [],
    onProductsChange,
    showForceValue = false,
    readOnly = false,
    onBulkProfileValue,
    onBulkParentValue,
    productProfileId,
    childProductProfileId,
}) => {
    const { t } = useTranslation();
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

    const handleSelectionChange = useCallback((keys: Key[]) => {
        setSelectedRowKeys(keys);
    }, []);

    const selectedProductIds = useMemo(() => selectedRowKeys.map(Number).filter((id) => !Number.isNaN(id)), [selectedRowKeys]);

    const onToggle = useCallback(
        (rowId: string | number, key: keyof ProductProfileProductEntity, valueOrChecked: boolean | string) => {
            const newData = products.map((row) => {
                const currentRowId = row.productId;
                if (currentRowId !== rowId) return row;

                const next: ProductProfileProductEntity = { ...row, [key]: valueOrChecked };

                // When changing enabled or mandatory, set inheritanceType to LOCAL
                if (key === "enabled" || key === "mandatory") {
                    next.inheritanceType = "LOCAL";
                }

                // When disabling a product, also disable mandatory flag
                if (key === "enabled" && !valueOrChecked) {
                    next.mandatory = false;
                    next.mandatoryIndicator = false;
                }

                // When enabling mandatory or forceValue, ensure product is enabled
                if ((key === "mandatory" || key === "forceValue") && valueOrChecked) {
                    next.enabled = true;
                }

                return next;
            });

            onProductsChange?.(newData);
        },
        [products, onProductsChange],
    );

    const columnsConfig = useMemo(() => getColumnsConfig(t, onToggle, showForceValue, readOnly), [t, onToggle, showForceValue, readOnly]);

    const hasSelection = selectedProductIds.length > 0;
    const isProfileValueDisabled = readOnly || !onBulkProfileValue || !hasSelection || !childProductProfileId;
    const isParentValueDisabled = readOnly || !onBulkParentValue || !hasSelection || !productProfileId;

    return (
        <>
            <ActionBar className="left">
                <SecondaryButton onClick={() => onBulkProfileValue?.(selectedProductIds)} disabled={isProfileValueDisabled}>
                    {t("productList.profileValue")}
                </SecondaryButton>
                <SecondaryButton onClick={() => onBulkParentValue?.(selectedProductIds)} disabled={isParentValueDisabled}>
                    {t("productList.parentValue")}
                </SecondaryButton>
            </ActionBar>

            <PropertyTable<ProductProfileProductEntity>
                getColumnsConfig={() => columnsConfig}
                propertyAsRowKey="productId"
                tableData={products}
                readOnly={readOnly}
                onDataChanged={onProductsChange || (() => {})}
                actions={[]}
                onSelectionChange={handleSelectionChange}
                pagination={false}
            />
        </>
    );
};
